import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Eye, Edit, Ban } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTradies = () => {
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingTradie, setEditingTradie] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchTradies();
  }, []);

  const fetchTradies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/tradies?limit=1000`);
      setTradies(response.data.tradies || []);
    } catch (error) {
      toast.error('Failed to load tradies');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tradie) => {
    setEditingTradie(tradie);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      await axios.patch(`${API}/admin/tradies/${editingTradie.id}`, {
        business_name: editingTradie.business_name,
        abn: editingTradie.abn,
        licence_number: editingTradie.licence_number
      });
      
      toast.success('Tradie updated successfully');
      setShowEditModal(false);
      setEditingTradie(null);
      fetchTradies();
    } catch (error) {
      toast.error('Failed to update tradie');
    }
  };

  const handleSuspend = async (tradieId) => {
    const reason = prompt('Reason for suspending this tradie:');
    if (!reason) return;

    try {
      await axios.patch(`${API}/admin/tradies/${tradieId}/suspend`, { reason });
      toast.success('Tradie suspended');
      fetchTradies();
    } catch (error) {
      toast.error('Failed to suspend tradie');
    }
  };

  const filtered = tradies.filter(t => 
    !search || 
    t.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.abn?.includes(search) ||
    t.user?.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Tradie Profiles</h1>
          <p className="text-muted-foreground">Manage all tradie business profiles</p>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by business name, ABN, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Tradie Profiles ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Business</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">ABN</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Tier</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-8">Loading...</td></tr>
                  ) : filtered.map((tradie) => (
                    <tr key={tradie.id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="font-medium">{tradie.business_name}</div>
                        <div className="text-xs text-muted-foreground">{tradie.user?.email}</div>
                      </td>
                      <td className="px-4 py-3 capitalize">{tradie.tradie_type}</td>
                      <td className="px-4 py-3 font-mono text-sm">{tradie.abn || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <Badge className="capitalize">{tradie.current_tier}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={tradie.abn_status === 'verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {tradie.abn_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(tradie)}
                            className="p-2 hover:bg-muted rounded-lg"
                            title="Edit tradie"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleSuspend(tradie.id)}
                            className="p-2 hover:bg-muted rounded-lg"
                            title="Suspend tradie"
                          >
                            <Ban className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* Edit Modal */}
        {showEditModal && editingTradie && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border">
              <h3 className="text-xl font-bold mb-4 text-foreground">Edit Tradie Profile</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-2">Business Name</label>
                  <Input
                    value={editingTradie.business_name}
                    onChange={(e) => setEditingTradie({ ...editingTradie, business_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-2">ABN</label>
                  <Input
                    value={editingTradie.abn || ''}
                    onChange={(e) => setEditingTradie({ ...editingTradie, abn: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-2">Licence Number</label>
                  <Input
                    value={editingTradie.licence_number || ''}
                    onChange={(e) => setEditingTradie({ ...editingTradie, licence_number: e.target.value })}
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTradies;